import React, { useRef, useEffect, useMemo } from 'react';
import {
  EnvelopeIcon,
  DocumentPlusIcon,
  ArrowPathIcon,
  BellIcon,
  BellSlashIcon,
} from '@heroicons/react/24/outline';
import type { ActionDropdownProps, ActionType } from './CaseHeader.types';

/**
 * Static action items configuration
 */
const STATIC_ACTION_ITEMS: Array<{
  id: ActionType;
  label: string;
  icon: React.ReactNode;
  description?: string;
}> = [
  {
    id: 'email',
    label: 'Email',
    icon: <EnvelopeIcon className="w-5 h-5" />,
    description: 'Composer un email',
  },
  {
    id: 'document',
    label: 'Ajouter document',
    icon: <DocumentPlusIcon className="w-5 h-5" />,
    description: 'Joindre un fichier',
  },
  {
    id: 'status',
    label: 'Changer statut',
    icon: <ArrowPathIcon className="w-5 h-5" />,
    description: "Modifier le statut de l'affaire",
  },
];

/**
 * ActionDropdown Component
 *
 * Dropdown menu for primary actions like creating messages, SMS, etc.
 */
export const ActionDropdown: React.FC<ActionDropdownProps> = ({
  isOpen,
  onToggle,
  onSelect,
  onClose,
  isSubscribed,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Build action items with dynamic subscribe/unsubscribe
  const actionItems = useMemo(() => {
    const subscribeAction = {
      id: 'subscribe' as ActionType,
      label: isSubscribed ? 'Ne plus suivre' : "Suivre l'affaire",
      icon: isSubscribed ? <BellSlashIcon className="w-5 h-5" /> : <BellIcon className="w-5 h-5" />,
      description: isSubscribed
        ? 'Se désabonner des notifications'
        : 'Recevoir les notifications pour cette affaire',
    };

    return [...STATIC_ACTION_ITEMS, subscribeAction];
  }, [isSubscribed]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <div className="case-header-action-dropdown" ref={dropdownRef}>
      <button
        className="case-header-action-btn primary"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <span>Nouvelle action</span>
        <svg
          className={`case-header-action-chevron ${isOpen ? 'open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="case-header-dropdown-menu" role="menu">
          {actionItems.map((item) => (
            <button
              key={item.id}
              className="case-header-dropdown-item"
              onClick={() => {
                onSelect(item.id);
                onClose();
              }}
              role="menuitem"
            >
              <span className="case-header-dropdown-item-icon">{item.icon}</span>
              <span className="case-header-dropdown-item-content">
                <span className="case-header-dropdown-item-label">{item.label}</span>
                {item.description && (
                  <span className="case-header-dropdown-item-desc">{item.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActionDropdown;
